import { useState, useCallback, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '@chainlit/react-client'
import { useMallarFetch } from '@/hooks/useMallarFetch'
import ChatPanel from '@/components/mallar/ChatPanel'
import DocumentPreview from '@/components/mallar/DocumentPreview'
import { Info } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import Page from 'pages/Page'
import { Loader } from '@/components/Loader'


interface Message {
  role: 'user' | 'assistant'
  content?: string
  contentAfter?: string
  formCard?: any
  submittedValues?: Record<string, string>
  streaming?: boolean
  isFormSubmission?: boolean
  id?: number
}

function EditorContent() {
  const { contractId } = useParams<{ contractId: string }>()
  const apiFetch = useMallarFetch()
  const navigate = useNavigate()

  const [phase, setPhase] = useState('chat')
  const [messages, setMessages] = useState<Message[]>([])
  const [filledValues, setFilledValues] = useState<Record<string, any>>({})
  const [docHtml, setDocHtml] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [totalSteps, setTotalSteps] = useState(20)
  const [activeTab, setActiveTab] = useState<'chat' | 'preview'>('chat')
  const [showProgress, setShowProgress] = useState(true)
  const [templateId, setTemplateId] = useState('anstallningsavtal')
  const [templateTitle, setTemplateTitle] = useState('')
  const [templateDescription, setTemplateDescription] = useState('')
  const [templateLawRef, setTemplateLawRef] = useState('')
  const [templateDetails, setTemplateDetails] = useState<string[]>([])
  const [loaded, setLoaded] = useState(false)
  const [needsGreeting, setNeedsGreeting] = useState(false)
  const [initialSubmitted, setInitialSubmitted] = useState<Set<number>>(new Set())
  const isStreamingRef = useRef(false)
  const loadedOnceRef = useRef(false)

  // On real unmount just clear the streaming gate so any in-flight fetch can
  // finish and update state — React 18 silently drops updates to unmounted
  // components, so this is safe.  We deliberately do NOT abort so that
  // StrictMode's fake-unmount/remount cycle doesn't kill the initial greeting
  // stream before it has a chance to deliver events.
  useEffect(() => {
    return () => { isStreamingRef.current = false }
  }, [])

  useEffect(() => {
    if (!contractId || loadedOnceRef.current) return
    loadedOnceRef.current = true
    apiFetch(`/api/contracts/${contractId}/messages`)
      .then(r => r.json())
      .then(data => {
        const msgs: Message[] = (data.messages || []).map((m: any, i: number) => ({
          role: m.role,
          content: m.content || '',
          contentAfter: m.content_after || undefined,
          formCard: m.form_card || undefined,
          formValues: m.form_values || undefined,
          isFormSubmission: m.is_form_submission,
          id: i,
        }))

        const sub = new Set<number>()
        msgs.forEach((m, i) => {
          if (m.formCard) {
            for (let j = i + 1; j < msgs.length; j++) {
              if ((msgs[j] as any).formValues) {
                sub.add(i)
                ;(msgs[i] as any).submittedValues = (msgs[j] as any).formValues
                break
              }
              if (msgs[j].formCard) break
            }
          }
        })

        const tId = data.template_id || 'anstallningsavtal'
        setTemplateId(tId)
        setMessages(msgs)
        setInitialSubmitted(sub)
        setFilledValues(data.filled || {})
        setPhase(data.phase || 'chat')

        fetch(`/mallar/api/templates/${tId}`)
          .then(r => r.json())
          .then(meta => {
            if (meta.totalSteps) setTotalSteps(meta.totalSteps)
            if (meta.title) setTemplateTitle(meta.title)
            if (meta.description) setTemplateDescription(meta.description)
            if (meta.law_ref) setTemplateLawRef(meta.law_ref)
            if (meta.details) setTemplateDetails(meta.details)
          })
          .catch(() => {})

        fetch('/mallar/api/config')
          .then(r => r.json())
          .then(cfg => { if (cfg.showProgress === false) setShowProgress(false) })
          .catch(() => {})

        if (Object.keys(data.filled || {}).length > 0) {
          apiFetch(`/api/contracts/${contractId}/document`)
            .then(r => r.json())
            .then(d => { if (d.document && Object.keys(d.filled_values || {}).length > 0) setDocHtml(d.document) })
            .catch(() => {})
        }

        const hasAssistant = (data.messages || []).some((m: any) => m.role === 'assistant')
        if (!hasAssistant) setNeedsGreeting(true)
        setLoaded(true)
      })
      .catch(() => setLoaded(true))
  }, [contractId])

  const refreshDoc = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/contracts/${contractId}/document`)
      const data = await res.json()
      if (data.filled_values) setFilledValues(data.filled_values)
      if (data.document && Object.keys(data.filled_values || {}).length > 0) setDocHtml(data.document)
      if (data.phase === 'complete') setPhase('complete')
    } catch {}
  }, [contractId, apiFetch])

  const applySSEEvent = (
    ev: any,
    assistantId: number,
    acc: { text: string; textAfter: string },
  ) => {
    switch (ev.type) {
      case 'text-delta':
        acc.text += ev.delta || ''
        setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: acc.text } : m))
        break
      case 'text-after-delta':
        acc.textAfter += ev.delta || ''
        setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, contentAfter: acc.textAfter } : m))
        break
      case 'tool-result':
        if (ev.toolName === 'collect_information') {
          setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, formCard: ev.result } : m))
        }
        if (ev.toolName === 'submit_contract_data') {
          const { filledValues: fv, assembledDocument } = ev.result || {}
          if (fv) setFilledValues(fv)
          if (assembledDocument) setDocHtml(assembledDocument)
        }
        break
      case 'error':
        acc.text += '\n\nEtt fel uppstod. Försök igen.'
        setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: acc.text, streaming: false } : m))
        break
    }
  }

  const streamChat = useCallback(async ({ message = '', formData = {} }: { message?: string; formData?: Record<string, any> } = {}) => {
    if (isStreamingRef.current) return
    isStreamingRef.current = true
    setIsStreaming(true)

    const assistantId = Date.now()
    if (message) setMessages(prev => [...prev, { role: 'user', content: message, id: assistantId - 1 }])
    setMessages(prev => [...prev, { role: 'assistant', content: '', id: assistantId, streaming: true }])

    try {
      const body: any = {}
      if (formData && Object.keys(formData).length > 0) body.form_data = formData
      if (message) body.message = message

      const res = await apiFetch(`/api/contracts/${contractId}/chat`, {
        method: 'POST',
        body: JSON.stringify(body),
      })

      const reader = res.body!.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      const acc = { text: '', textAfter: '' }

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try { applySSEEvent(JSON.parse(line.slice(6)), assistantId, acc) } catch {}
        }
      }
    } catch {
      setMessages(prev => prev.map(m => m.id === assistantId
        ? { ...m, content: (m.content || '') + '\n\nEtt fel uppstod. Försök igen.', streaming: false }
        : m))
    } finally {
      setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, streaming: false } : m))
      isStreamingRef.current = false
      setIsStreaming(false)
      refreshDoc()
    }
  }, [contractId, apiFetch, refreshDoc])

  useEffect(() => {
    if (loaded && needsGreeting) { setNeedsGreeting(false); streamChat({}) }
  }, [loaded, needsGreeting, streamChat])

  const handleSend = useCallback((text: string) => streamChat({ message: text }), [streamChat])
  const handleFormSubmit = useCallback((formData: Record<string, string>) => {
    setMessages(prev => [...prev, { role: 'user', content: '', isFormSubmission: true, id: Date.now() }])
    streamChat({ formData })
  }, [streamChat])

  if (!loaded) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader className="h-6 w-6" />
      </div>
    )
  }

  const filled = Object.keys(filledValues).length
  const pct = Math.min(Math.round(filled / totalSteps * 100), 100)

  return (
    <div className="flex-1 flex overflow-hidden min-h-0 relative">
      {/* Chat column — absolute overlay on mobile, normal flex on desktop */}
      <div className={cn(
        'absolute inset-0 flex flex-col overflow-hidden',
        activeTab === 'chat' ? 'visible' : 'invisible pointer-events-none',
        'md:relative md:inset-auto md:visible md:pointer-events-auto md:flex md:flex-1 md:min-w-0 md:border-r'
      )}>
        {/* Contract meta strip */}
        <div className="h-11 flex items-center border-b bg-background shrink-0 px-4">

          {/* Mobile: tab switcher only, centered */}
          <div className="flex md:hidden w-full justify-center gap-1">
            <button
              onClick={() => setActiveTab('chat')}
              className={cn(
                'px-3 py-1 rounded text-xs font-medium transition-colors',
                activeTab === 'chat'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Chatt
            </button>
            <button
              onClick={() => setActiveTab('preview')}
              className={cn(
                'px-3 py-1 rounded text-xs font-medium transition-colors',
                activeTab === 'preview'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Förhandsgranskning
            </button>
          </div>

          {/* Desktop: full meta strip */}
          <div className="hidden md:flex items-center gap-3 w-full">
            <button
              onClick={() => navigate('/mallar')}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors shrink-0"
            >
              ← Avtalsmallar
            </button>
            <span className="text-muted-foreground/40 text-xs">|</span>
            <span className="text-xs font-medium text-foreground truncate">
              {templateTitle || templateId}
            </span>
            {(templateDescription || templateDetails.length > 0) && (
              <Popover>
                <PopoverTrigger asChild>
                  <button className="text-muted-foreground hover:text-foreground transition-colors shrink-0" title="Om detta avtal">
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent side="bottom" align="start" className="w-80 p-4 space-y-3">
                  <div className="space-y-0.5">
                    <h4 className="text-sm font-semibold">{templateTitle || templateId}</h4>
                    {templateLawRef && (
                      <p className="text-[10px] text-muted-foreground">{templateLawRef}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    {templateDetails.length > 0
                      ? templateDetails.map((para, i) => (
                          <p key={i} className="text-xs text-foreground leading-relaxed">{para}</p>
                        ))
                      : <p className="text-xs text-foreground leading-relaxed">{templateDescription}</p>
                    }
                  </div>
                </PopoverContent>
              </Popover>
            )}
            {showProgress && (
              <div className="ml-auto flex items-center gap-2 min-w-[120px]">
                <Progress
                  value={pct}
                  className={cn(
                    'h-1.5 w-20',
                    pct === 100 && '[&>div]:bg-green-500'
                  )}
                />
                <span className={cn('text-[10px] tabular-nums', pct === 100 ? 'text-green-600' : 'text-muted-foreground')}>
                  {pct}%
                </span>
              </div>
            )}
          </div>

        </div>

        {/* Chat */}
        <div className="flex-1 overflow-hidden p-3">
          <ChatPanel
            messages={messages}
            isStreaming={isStreaming}
            onSend={handleSend}
            onFormSubmit={handleFormSubmit}
            initialSubmitted={initialSubmitted}
            readOnly={false}
          />
        </div>
      </div>

      {/* Document preview column — absolute overlay on mobile, normal flex on desktop */}
      <div className={cn(
        'absolute inset-0 flex flex-col overflow-hidden',
        activeTab === 'preview' ? 'visible' : 'invisible pointer-events-none',
        'md:relative md:inset-auto md:visible md:pointer-events-auto md:flex md:w-[48%] md:shrink-0'
      )}>
        <DocumentPreview
          document={docHtml}
          phase={phase}
          contractId={contractId!}
          templateId={templateId}
          apiFetch={apiFetch}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>
    </div>
  )
}

export default function MallarEditor() {
  const { user } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (user !== undefined && !user) navigate('/', { replace: true })
  }, [user, navigate])

  if (user === undefined) return null

  return (
    <Page>
      <EditorContent />
    </Page>
  )
}
